import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import { useCreateSupplierMutation, useUpdateSupplierMutation } from '../../../store/api/purchaseApi';

const initialFormData = {
  name: '',
  code: '',
  email: '',
  phone: '',
  address: '',
  taxId: '',
  gstin: '',
  state: '',
};

export function SupplierFormContent({
  supplier = null,
  isEdit = false,
  loading = false,
  compact = false,
  onCancel,
  onSuccess,
}) {
  const [createSupplier] = useCreateSupplierMutation();
  const [updateSupplier] = useUpdateSupplierMutation();
  const [formData, setFormData] = useState(initialFormData);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name || '',
        code: supplier.code || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        taxId: supplier.taxId || '',
        gstin: supplier.gstin || '',
        state: supplier.state || '',
      });
    } else if (!isEdit) {
      setFormData(initialFormData);
    }
  }, [supplier, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSubmitError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim() || undefined,
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        address: formData.address.trim() || undefined,
        taxId: formData.taxId.trim() || undefined,
        gstin: formData.gstin.trim() || undefined,
        state: formData.state.trim() || undefined,
      };

      const saved = isEdit
        ? await updateSupplier({ id: supplier.id, ...payload }).unwrap()
        : await createSupplier(payload).unwrap();

      onSuccess?.(saved);
    } catch (err) {
      setSubmitError(err?.data?.message || 'Error saving supplier');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2} sx={{ overflow: 'visible' }}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ overflow: 'visible' }}>
          <TextField
            fullWidth
            required
            label="Supplier Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Supplier Code"
            name="code"
            value={formData.code}
            onChange={handleChange}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ overflow: 'visible' }}>
          <TextField
            fullWidth
            label="VAT Number"
            name="gstin"
            value={formData.gstin}
            onChange={handleChange}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ overflow: 'visible' }}>
          <TextField
            fullWidth
            label="State"
            name="state"
            value={formData.state}
            onChange={handleChange}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Tax ID"
            name="taxId"
            value={formData.taxId}
            onChange={handleChange}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ overflow: 'visible' }}>
          <TextField
            fullWidth
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            multiline
            rows={compact ? 2 : 3}
            size={compact ? 'small' : 'medium'}
            InputLabelProps={{ sx: { overflow: 'visible', whiteSpace: 'nowrap' } }}
          />
        </Grid>
        {submitError && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="error">
              {submitError}
            </Typography>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button type="button" variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
}

